import { SprinklesElement } from "sprinkles-js";

/**
 * This element allows us to filter some inputs in the candidacies filter based on the values of other inputs
 *
 * Since we have quite a bit of interdependent logic going on (and we don't have any similar cases), this element
 * is very specific to the candidacies filter.
 */
export default class CandidaciesFilterElement extends SprinklesElement {
  static tagName = "candidacies-filter";
  static refs = [
    "constituencyInput",
    "partyWrapper",
    "constituencyPartyInput",
    "constituencyPartyWrapper",
    "listInput",
  ];
  static events = {
    change: [
      { method: "handleConstituencyChange", ref: "constituencyInput" },
      {
        method: "handleConstituencyPartyChange",
        ref: "constituencyPartyInput",
      },
    ],
  };

  // We store the current values, so that we can ignore any accidental events that shouldn't trigger updates
  // If these options remain `null`, we know that the relevant input is not available in the DOM and can ignore this filter
  #currentConstituencyID = null;
  #currentConstituencyPartyID = null;

  afterConnected() {
    // We store a copy of the options that should be filtered for constituency parties and lists
    // WebKit doesn't allow is to apply `display: none;` to an option, so we have to actually remove this option from the DOM
    this.constituencyPartyOptions = buildOptions(
      this.refs.constituencyPartyInput,
    );
    if (this.refs.listInput) {
      this.listOptions = buildOptions(this.refs.listInput, true);
    }

    // Initialize internal values
    if (this.refs.constituencyInput) {
      this.#currentConstituencyID = this.refs.constituencyInput.value;
    }
    if (this.refs.constituencyPartyInput) {
      this.#currentConstituencyPartyID = this.refs.constituencyPartyInput.value;
    }

    this.#updatePartyInputs();
    this.#updateListInput();
  }

  handleConstituencyChange() {
    const value = this.refs.constituencyInput.value;
    if (value !== this.#currentConstituencyID) {
      this.#currentConstituencyID = value;
      this.#updatePartyInputs();
      this.#updateListInput();
    }
  }

  handleConstituencyPartyChange() {
    const value = this.refs.constituencyPartyInput.value;
    if (value !== this.#currentConstituencyPartyID) {
      this.#currentConstituencyPartyID = value;
      this.#updateListInput();
    }
  }

  #updatePartyInputs() {
    if (this.#currentConstituencyID === null) return;

    if (this.#currentConstituencyID === "") {
      // If no constituency is selected, we show the input for parties and hide the one for constituency parties
      this.refs.partyWrapper.hidden = false;
      this.refs.constituencyPartyWrapper.hidden = true;
    } else {
      // If a constituency is selected, we hide the input for parties and show the one for constituency parties
      // We then only add the options that are relevant for that constituency
      this.refs.partyWrapper.hidden = true;
      this.refs.constituencyPartyWrapper.hidden = false;

      getOptions(this.refs.constituencyPartyInput).forEach((opt) =>
        opt.remove(),
      );
      this.constituencyPartyOptions.forEach((opt) => {
        if (opt.constituencyID === this.#currentConstituencyID) {
          this.refs.constituencyPartyInput.appendChild(opt.node);
        }
      });
    }
  }

  #updateListInput() {
    if (!this.refs.listInput) return;
    if (
      this.#currentConstituencyID === null &&
      this.#currentConstituencyPartyID === null
    ) {
      return;
    }

    getOptions(this.refs.listInput).forEach((opt) => opt.remove());
    this.refs.listInput.disabled = false;

    if (this.#currentConstituencyID === "") {
      // If no constituency is selected (but this input is present!), we disable the list input
      this.refs.listInput.value = "";
      this.refs.listInput.disabled = true;
    } else if (!["", null].includes(this.#currentConstituencyPartyID)) {
      // If a constituency party is selected, we enable the input and filter by the constituency parties
      // We don't need to filter by constituency, as the selected constituency party should already be part of the selected constituency
      this.listOptions.forEach((opt) => {
        if (
          opt.constituencyPartyIDs.includes(this.#currentConstituencyPartyID)
        ) {
          this.refs.listInput.appendChild(opt.node);
        }
      });
    } else if (this.#currentConstituencyID !== null) {
      // If a constituency is selected, we filter the options by that constituency
      this.listOptions.forEach((opt) => {
        if (opt.constituencyID === this.#currentConstituencyID) {
          this.refs.listInput.appendChild(opt.node);
        }
      });
    } else {
      // Otherwise, we add all options
      this.listOptions.forEach((opt) => {
        this.refs.listInput.appendChild(opt.node);
      });
    }
  }
}

/**
 * Get an array of options (with a value) from a select element
 *
 * @param {HTMLSelectElement} element
 * @returns {Array<HTMLOptionElement>}
 */
function getOptions(element) {
  return Array.from(element.options).filter((node) => node.value !== "");
}

/**
 * Build an array of cloned options, with some values extracted
 *
 * @param {HTMLSelectElement} element
 * @param {boolean} includeConstituencyPartyIDs
 * @returns Array of cloned options
 */
function buildOptions(element, includeConstituencyPartyIDs = false) {
  return getOptions(element).map((node) => {
    const item = { node, constituencyID: node.dataset.constituencyId };
    if (includeConstituencyPartyIDs) {
      item.constituencyPartyIDs =
        node.dataset.constituencyPartyIds?.split(" ") || [];
    }
    return item;
  });
}
